import { useEffect, type RefObject } from 'react';

interface Options {
  active: boolean;
  // React 19 `useRef<T>(null)` yields RefObject<T | null>; the frame loop already guards on `root.current`.
  root: RefObject<HTMLElement | null>;
  onCancel?: () => void;
}

function buttonPressed(pad: Gamepad, index: number): boolean {
  return pad.buttons[index]?.pressed === true;
}

/**
 * Native-button focus navigation for non-simulation React overlays.
 * The game runtime owns gameplay input; this hook is active only while a menu is visible.
 */
export function useGamepadMenu({ active, root, onCancel }: Options): void {
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    let priorUp = false;
    let priorDown = false;
    let priorLeft = false;
    let priorRight = false;
    let priorConfirm = false;
    let priorCancel = false;

    const moveFocus = (delta: number): void => {
      const host = root.current;
      if (!host) return;
      const items = host.querySelectorAll<HTMLElement>('[data-menu-item]:not([disabled])');
      const count = items.length;
      if (count === 0) return;
      let index = -1;
      for (let i = 0; i < count; i += 1) {
        if (items[i] === document.activeElement) {
          index = i;
          break;
        }
      }
      const next = index < 0 ? 0 : (index + delta + count) % count;
      items[next]?.focus({ preventScroll: true });
    };

    const frame = (): void => {
      const pad = navigator.getGamepads?.()[0];
      if (pad) {
        const axisX = pad.axes[0] ?? 0;
        const axisY = pad.axes[1] ?? 0;
        const up = buttonPressed(pad, 12) || axisY < -0.55;
        const down = buttonPressed(pad, 13) || axisY > 0.55;
        const left = buttonPressed(pad, 14) || axisX < -0.55;
        const right = buttonPressed(pad, 15) || axisX > 0.55;
        const confirm = buttonPressed(pad, 0);
        const cancel = buttonPressed(pad, 1);
        const focused = document.activeElement;
        const focusedRange = focused instanceof HTMLInputElement && focused.type === 'range';
        if (up && !priorUp) moveFocus(-1);
        if (down && !priorDown) moveFocus(1);
        if (left && !priorLeft) {
          if (focusedRange) {
            focused.stepDown();
            focused.dispatchEvent(new Event('input', { bubbles: true }));
            focused.dispatchEvent(new Event('change', { bubbles: true }));
          } else moveFocus(-1);
        }
        if (right && !priorRight) {
          if (focusedRange) {
            focused.stepUp();
            focused.dispatchEvent(new Event('input', { bubbles: true }));
            focused.dispatchEvent(new Event('change', { bubbles: true }));
          } else moveFocus(1);
        }
        if (confirm && !priorConfirm) {
          if (focused instanceof HTMLButtonElement && !focused.disabled) focused.click();
          else if (focused instanceof HTMLInputElement && focused.type === 'checkbox' && !focused.disabled) focused.click();
        }
        if (cancel && !priorCancel) onCancel?.();
        priorUp = up;
        priorDown = down;
        priorLeft = left;
        priorRight = right;
        priorConfirm = confirm;
        priorCancel = cancel;
      } else {
        priorUp = false;
        priorDown = false;
        priorLeft = false;
        priorRight = false;
        priorConfirm = false;
        priorCancel = false;
      }
      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [active, onCancel, root]);
}
